import {
  Button,
  Modal,
  Select,
  TextInput,
} from "@risalabs_frontend_org/oasis-ui-kit";
import JsonView from "@uiw/react-json-view";
import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { GroupInboxRoute } from "../../../api/services/groupInbox/groupInboxConfigTypes";
import {
  ADD_NEW_VIEW_TAB_MODAL_TITLE,
  ADD_NEW_VIEW_TAB_TYPE,
  CANCEL_BUTTON_TEXT,
  CONFIGURATION_EDITOR_TITLE,
  CONFIG_CHANGED_WARNING,
  EDIT_CONFIGURATION_EDITOR_MODE,
  EDIT_VIEW_TAB_MODAL_TITLE,
  EDIT_VIEW_TAB_TYPE,
  SAVE_BUTTON_TEXT,
  SAVING_TEXT_FOR_BUTTON,
  VIEW_CONFIGURATION_EDITOR_MODE,
} from "../../../constants/stringLiterals";
import { ModalId } from "../../../enums/modalId";
import { GiConfiguration } from "../../../pages/configurations/pages/giRpaConfiguration/hooks/useGiConfiguration";
import { AppDispatch, RootState } from "../../../redux/store/store";
import { handleConfigTextChange } from "./actionHandlers/handleConfigTextChange";
import { handleGiConfigUpdate } from "./actionHandlers/handleGiConfigUpdate";
import { handleSelectConfiguration } from "./actionHandlers/handleSelectConfiguration";
import { useGiConfigDetails } from "./hooks/useGiConfigDetails";

/**
 * Props for the add/edit GI view-tab modal.
 *
 * @example
 * <AddNewViewTab
 *   giConfigurations={configs}
 *   org_id={facilityId}
 *   route={selectedRoute}
 *   onClose={close}
 *   onConfigSaved={refresh}
 * />
 */
export interface AddNewViewTabProps {
  /** Existing configs used to clone from when adding a new view tab. */
  giConfigurations: GiConfiguration[] | null;
  /** Organization that owns the configuration. */
  org_id: string;
  /** Configuration source (`rpa` or `api`) written on save. */
  route: GroupInboxRoute;
  /** Closes the modal without saving. */
  onClose: () => void;
  /** Called after a successful save so the parent can refetch. */
  onConfigSaved: () => void;
}

/**
 * Modal for adding or editing a Group Inbox view-tab configuration.
 *
 * @param props - Facility, route, existing configs, and close/save callbacks
 * @returns The add/edit view-tab modal
 */
const AddNewViewTab = ({
  giConfigurations,
  org_id,
  route,
  onClose,
  onConfigSaved,
}: AddNewViewTabProps) => {
  const dispatch = useDispatch<AppDispatch>();
  const { metaData } = useSelector((state: RootState) => state.modal);
  const type = metaData?.type as
    typeof ADD_NEW_VIEW_TAB_TYPE | typeof EDIT_VIEW_TAB_TYPE;
  const {
    giConfiguration,
    setGiConfiguration,
    configText,
    setConfigText,
    configError,
    setConfigError,
    isConfigChanged,
    isRequiredFieldsFilledAndValid,
  } = useGiConfigDetails();
  const [jsonEditorMode, setJsonEditorMode] = useState<
    | typeof VIEW_CONFIGURATION_EDITOR_MODE
    | typeof EDIT_CONFIGURATION_EDITOR_MODE
  >(VIEW_CONFIGURATION_EDITOR_MODE);
  const [loading, setLoading] = useState<boolean>(false);

  return (
    <Modal
      dialogId={ModalId.ADD_NEW_VIEW_TAB}
      title={
        type === ADD_NEW_VIEW_TAB_TYPE
          ? ADD_NEW_VIEW_TAB_MODAL_TITLE
          : EDIT_VIEW_TAB_MODAL_TITLE
      }
      onSave={() =>
        handleGiConfigUpdate(
          giConfiguration,
          type,
          isConfigChanged,
          org_id,
          route,
          dispatch,
          setLoading,
          onConfigSaved,
        )
      }
      onCancel={() => onClose()}
      onClose={() => onClose()}
      saveButtonText={loading ? SAVING_TEXT_FOR_BUTTON : SAVE_BUTTON_TEXT}
      cancelText={CANCEL_BUTTON_TEXT}
      heightPercentage={80}
      disableSave={
        !isRequiredFieldsFilledAndValid || loading || configError !== null
      }
    >
      <div className="add-new-view-tab-modal--container flex h-full flex-col gap-4 overflow-hidden">
        {type === ADD_NEW_VIEW_TAB_TYPE && (
          <>
            <Select
              options={
                giConfigurations?.map((giConfiguration) => ({
                  label: giConfiguration.view_tab || "",
                  value: giConfiguration.id || "",
                })) || []
              }
              onOptionChange={(option) =>
                handleSelectConfiguration({
                  option: {
                    value: option.value,
                    label: option.name,
                  },
                  giConfigurations,
                  setJsonEditorMode,
                  setGiConfiguration,
                  setConfigText,
                  setConfigError,
                  viewMode: VIEW_CONFIGURATION_EDITOR_MODE,
                })
              }
              label="Select a configuration"
              id="configuration"
              placeholder="Select a configuration"
            />
          </>
        )}
        <div className="view_tab_and_num_orders_container flex justify-between gap-2">
          <TextInput
            id="view_tab"
            label="View Tab"
            placeholder="Enter view tab"
            className="w-1/2"
            defaultValue={giConfiguration.view_tab}
            onChange={(event) =>
              setGiConfiguration((prev) => ({
                ...prev,
                view_tab: event.value,
              }))
            }
          />
          <TextInput
            id="num_orders"
            label="Number of Orders"
            placeholder="Enter number of orders"
            className="w-1/2"
            defaultValue={giConfiguration.num_orders.toString()}
            onChange={(event) =>
              setGiConfiguration((prev) => ({
                ...prev,
                num_orders: parseInt(event.value, 10) || 0,
              }))
            }
            type="number"
          />
        </div>
        <div className="configuration_editor_container__header flex items-center justify-between gap-2">
          <div className="configuration_editor_container__header__title text-xs">
            {CONFIGURATION_EDITOR_TITLE}
            {isConfigChanged && (
              <div className="mt-2 rounded-md border border-tertiaryRed-8 bg-tertiaryRed-50 p-2 text-xs">
                {CONFIG_CHANGED_WARNING}
              </div>
            )}
          </div>
          <div className="configuration_editor_container__header__mode flex items-center gap-2">
            <Button
              buttonType="secondary"
              size="small"
              onClick={() => setJsonEditorMode(VIEW_CONFIGURATION_EDITOR_MODE)}
              disabled={jsonEditorMode === VIEW_CONFIGURATION_EDITOR_MODE}
              children="View"
            />
            <Button
              buttonType="secondary"
              size="small"
              onClick={() => setJsonEditorMode(EDIT_CONFIGURATION_EDITOR_MODE)}
              disabled={jsonEditorMode === EDIT_CONFIGURATION_EDITOR_MODE}
              children="Edit"
            />
          </div>
        </div>
        <div className="rpa_configuration_container border-primaryGray15 w-full flex-1 overflow-auto rounded-md border p-3">
          {jsonEditorMode === VIEW_CONFIGURATION_EDITOR_MODE && (
            <>
              <JsonView value={giConfiguration.config} />
            </>
          )}
          {jsonEditorMode === EDIT_CONFIGURATION_EDITOR_MODE && (
            <>
              <textarea
                className={`outline-auto h-full w-full resize-none overflow-auto p-2 font-mono text-xs ${
                  configError ? "outline-red-500" : ""
                }`}
                spellCheck={false}
                value={configText}
                onChange={(event) => {
                  handleConfigTextChange({
                    nextText: event.target.value,
                    setConfigText,
                    setConfigError,
                    setGiConfiguration,
                  });
                }}
              />
              {configError && (
                <div className="mt-2 text-xs text-red-600">
                  Invalid JSON: {configError}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </Modal>
  );
};

export default AddNewViewTab;
